/**
 * SystemPackagesManager - Hantera system-paket och priser
 * Används i Prislistan för att skapa/redigera paket
 */

import React from 'react';
import {
  CubeIcon,
  PlusIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import * as HeroIcons from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import type { MenuModule } from '@/types/systems';
import { useSystemPackagesManager } from '@/hooks/useSystemPackagesManager';
import {
  ModuleWithPricing,
  PricingModel,
  PRICING_MODEL_OPTIONS,
  getPricingModelLabel,
  formatPrice,
} from '@/types/systemPackages';

// Helper function för att rendera ikoner
const getIcon = (iconName: string | undefined, className: string = 'w-5 h-5'): JSX.Element => {
  if (!iconName) return <CubeIcon className={className} />;
  const IconComponent = (HeroIcons as any)[iconName];
  return IconComponent ? <IconComponent className={className} /> : <CubeIcon className={className} />;
};

// Rekursiv komponent för modulpriser
const ModulePriceRow: React.FC<{
  module: MenuModule;
  pricing: ModuleWithPricing | undefined;
  level: number;
  expandedItems: Set<string>;
  setExpandedItems: (items: Set<string>) => void;
  editingModulePrice: string | null;
  setEditingModulePrice: (id: string | null) => void;
  modulePriceInput: string;
  setModulePriceInput: (price: string) => void;
  pricingModelInput: PricingModel;
  setPricingModelInput: (model: PricingModel) => void;
  handleSaveModulePrice: (id: string, pricingModel: PricingModel) => void;
  t: (key: string) => string;
  allPricing: ModuleWithPricing[];
}> = ({
  module,
  pricing,
  level,
  expandedItems,
  setExpandedItems,
  editingModulePrice,
  setEditingModulePrice,
  modulePriceInput,
  setModulePriceInput,
  pricingModelInput,
  setPricingModelInput,
  handleSaveModulePrice,
  t,
  allPricing,
}) => {
  const hasChildren = module.children && module.children.length > 0;
  const isExpanded = expandedItems.has(module.id);
  const paddingLeft = 12 + level * 24;
  
  return (
    <>
      <div 
        className={`flex items-center border-b border-border hover:bg-bgBase ${hasChildren ? 'cursor-pointer' : ''}`}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        {/* Expand/collapse för children */}
        <div className="w-6 flex-shrink-0">
          {hasChildren && (
            <button
              onClick={() => {
                const newExpanded = new Set(expandedItems);
                if (isExpanded) {
                  newExpanded.delete(module.id);
                } else {
                  newExpanded.add(module.id);
                }
                setExpandedItems(newExpanded);
              }}
              className="p-1"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-3 h-3 text-textTertiary" />
              ) : (
                <ChevronRightIcon className="w-3 h-3 text-textTertiary" />
              )}
            </button>
          )}
        </div>
        
        {/* Modul-info */}
        <div className="flex-1 flex items-center gap-2 py-2.5 pr-3">
          {getIcon(module.icon, 'w-4 h-4 text-teal-600 dark:text-teal-400')}
          <span className={`${hasChildren ? 'font-medium' : ''} text-textDefault text-sm`}>
            {module.displayName}
          </span>
        </div>
        
        {/* Prismodell */}
        <div className="w-36 py-2.5 pr-3">
          {editingModulePrice === module.id ? (
            <select
              value={pricingModelInput}
              onChange={(e) => setPricingModelInput(e.target.value as PricingModel)}
              className="w-full px-2 py-1 bg-bgBase border border-border rounded text-textDefault text-sm"
            >
              {PRICING_MODEL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-textSecondary">
              {getPricingModelLabel(pricing?.pricingModel)}
            </span>
          )}
        </div>

        {/* Pris */}
        <div className="w-28 text-right py-2.5 pr-3">
          {editingModulePrice === module.id ? (
            <input
              type="text"
              value={modulePriceInput}
              onChange={(e) => setModulePriceInput(e.target.value)}
              className="w-24 px-2 py-1 text-right bg-bgBase border border-border rounded text-textDefault text-sm"
              autoFocus
              placeholder="0.00"
            />
          ) : (
            <span className={`text-sm ${pricing && pricing.priceMonthly > 0 ? 'font-medium' : 'text-textTertiary'}`}>
              {pricing && pricing.priceMonthly > 0 ? formatPrice(pricing.priceMonthly) : t('packages.notSet')}
            </span>
          )}
        </div>
        
        {/* Actions */}
        <div className="w-28 text-right py-2.5 pr-3">
          {editingModulePrice === module.id ? (
            <div className="flex gap-1 justify-end">
              <button
                onClick={() => handleSaveModulePrice(pricing?.id || module.name, pricingModelInput)}
                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                {t('common.save')}
              </button>
              <button
                onClick={() => setEditingModulePrice(null)}
                className="px-2 py-1 text-xs bg-bgLight text-textDefault rounded hover:bg-bgHover"
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setEditingModulePrice(module.id);
                setModulePriceInput(pricing?.priceMonthly ? pricing.priceMonthly.toString() : '');
                setPricingModelInput(pricing?.pricingModel || 'monthly');
              }}
              className="p-1 hover:bg-bgHover rounded"
              title={t('packages.editPrice')}
            >
              <PencilIcon className="w-4 h-4 text-textSecondary" />
            </button>
          )}
        </div>
      </div>
      
      {/* Children */}
      {hasChildren && isExpanded && module.children?.map(child => (
        <ModulePriceRow
          key={child.id}
          module={child}
          pricing={allPricing.find(m => m.route === child.route || m.name === child.name || m.displayName === child.displayName)}
          level={level + 1}
          expandedItems={expandedItems}
          setExpandedItems={setExpandedItems}
          editingModulePrice={editingModulePrice}
          setEditingModulePrice={setEditingModulePrice}
          modulePriceInput={modulePriceInput}
          setModulePriceInput={setModulePriceInput}
          pricingModelInput={pricingModelInput}
          setPricingModelInput={setPricingModelInput}
          handleSaveModulePrice={handleSaveModulePrice}
          t={t}
          allPricing={allPricing}
        />
      ))}
    </>
  );
};

export default function SystemPackagesManager() {
  const { t } = useTranslation();
  
  const {
    systems,
    modulesWithPricing,
    menuSystems,
    availableWidgets,
    systemModules,
    modulesWithoutPackagesBySystem,
    loading,
    expandedSystems,
    expandedMenuItems,
    showModal,
    editingPackage,
    activeTab,
    editingModulePrice,
    modulePriceInput,
    pricingModelInput,
    formData,
    setActiveTab,
    setFormData,
    setModulePriceInput,
    setPricingModelInput,
    toggleExpand,
    toggleMenuItem,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSave,
    handleSaveModulePrice,
    toggleModule,
    cancelEditModulePrice,
  } = useSystemPackagesManager();

  // Local state setter passthrough for ModulePriceRow
  const setEditingModulePrice = (id: string | null) => {
    if (id === null) {
      cancelEditModulePrice();
    }
  };

  const setExpandedMenuItems = (_items: Set<string>) => {
    // This is handled via toggleMenuItem in the hook
    // For ModulePriceRow we need to expose this differently
  };

  const modulesWithoutPackages = Object.values(modulesWithoutPackagesBySystem).flat();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bgPrimary"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      {/* Flikar */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab('packages')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'packages'
              ? 'text-bgPrimary border-b-2 border-bgPrimary'
              : 'text-textSecondary hover:text-textDefault'
          }`}
        >
          {t('packages.packages')}
        </button>
        <button
          onClick={() => setActiveTab('modules')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'modules'
              ? 'text-bgPrimary border-b-2 border-bgPrimary'
              : 'text-textSecondary hover:text-textDefault'
          }`}
        >
          {t('packages.modulePrices')}
        </button>
      </div>

      {/* Modulpriser-flik - Baserad på sidomenyn */}
      {activeTab === 'modules' && (
        <div className="space-y-2">
          <p className="text-sm text-textSecondary mb-4">
            {t('packages.modulePricesDescription')}
          </p>

          {menuSystems.map(system => {
            const isExpanded = expandedMenuItems.has(system.id);
            const isDashboard = system.name?.toLowerCase() === 'dashboard' || system.displayName?.toLowerCase() === 'dashboard';
            const widgetsForDashboard = isDashboard ? availableWidgets : [];
            const totalItems = system.modules.length + widgetsForDashboard.length;
            
            return (
              <div key={system.id} className="bg-bgElevated rounded-lg border border-border overflow-hidden">
                {/* System header */}
                <button
                  onClick={() => toggleMenuItem(system.id)}
                  className="w-full flex items-center gap-3 p-3 bg-bgBase hover:bg-bgHover transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-textTertiary" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-textTertiary" />
                  )}
                  {getIcon(system.icon, 'w-5 h-5 text-teal-600 dark:text-teal-400')}
                  <span className="font-semibold text-textDefault">{system.displayName}</span>
                  <span className="text-xs text-textTertiary">
                    ({totalItems} {isDashboard && widgetsForDashboard.length > 0 ? 'widgets' : t('packages.modules')})
                  </span>
                </button>
                
                {/* Modules + Widgets för Dashboard */}
                {isExpanded && totalItems > 0 && (
                  <div className="border-t border-border">
                    {/* Vanliga moduler */}
                    {system.modules.map(module => (
                      <ModulePriceRow
                        key={module.id}
                        module={module}
                        pricing={modulesWithPricing.find(m => m.route === module.route || m.name === module.name || m.displayName === module.displayName)}
                        level={0}
                        expandedItems={expandedMenuItems}
                        setExpandedItems={setExpandedMenuItems}
                        editingModulePrice={editingModulePrice}
                        setEditingModulePrice={setEditingModulePrice}
                        modulePriceInput={modulePriceInput}
                        setModulePriceInput={setModulePriceInput}
                        pricingModelInput={pricingModelInput}
                        setPricingModelInput={setPricingModelInput}
                        handleSaveModulePrice={handleSaveModulePrice}
                        t={t}
                        allPricing={modulesWithPricing}
                      />
                    ))}
                    
                    {/* Widgets för Dashboard */}
                    {widgetsForDashboard.map(widget => {
                      const widgetPricing = modulesWithPricing.find(m => m.name === `widget-${widget.type}` || m.name === widget.type);
                      const isEditing = editingModulePrice === `widget-${widget.type}`;
                      
                      return (
                        <div 
                          key={widget.type}
                          className="flex items-center border-b border-border hover:bg-bgBase pl-3"
                        >
                          {/* Widget info */}
                          <div className="flex-1 flex items-center gap-2 py-2.5 pr-3 pl-6">
                            {getIcon(widget.icon === 'truck' ? 'TruckIcon' : 
                                     widget.icon === 'map' ? 'MapIcon' : 
                                     widget.icon === 'chart-bar' ? 'ChartBarIcon' : 
                                     widget.icon === 'clock' ? 'ClockIcon' : 
                                     widget.icon === 'bell' ? 'BellIcon' : 
                                     widget.icon === 'clipboard-check' ? 'ClipboardDocumentCheckIcon' : 
                                     widget.icon === 'plug' ? 'BoltSlashIcon' : 
                                     widget.icon === 'battery-low' ? 'Battery0Icon' : 
                                     'CubeIcon', 'w-4 h-4 text-teal-600 dark:text-teal-400')}
                            <span className="text-textDefault text-sm">{widget.name}</span>
                            {!widget.implemented && (
                              <span className="text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">
                                Kommer snart
                              </span>
                            )}
                          </div>
                          
                          {/* Prismodell */}
                          <div className="w-36 py-2.5 pr-3">
                            {isEditing ? (
                              <select
                                value={pricingModelInput}
                                onChange={(e) => setPricingModelInput(e.target.value as PricingModel)}
                                className="w-full px-2 py-1 bg-bgBase border border-border rounded text-textDefault text-sm"
                              >
                                {PRICING_MODEL_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-sm text-textSecondary">
                                {getPricingModelLabel(widgetPricing?.pricingModel)}
                              </span>
                            )}
                          </div>

                          {/* Pris */}
                          <div className="w-28 text-right py-2.5 pr-3">
                            {isEditing ? (
                              <input
                                type="text"
                                value={modulePriceInput}
                                onChange={(e) => setModulePriceInput(e.target.value)}
                                className="w-24 px-2 py-1 text-right bg-bgBase border border-border rounded text-textDefault text-sm"
                                autoFocus
                                placeholder="0.00"
                              />
                            ) : (
                              <span className={`text-sm ${widgetPricing && widgetPricing.priceMonthly > 0 ? 'font-medium' : 'text-textTertiary'}`}>
                                {widgetPricing && widgetPricing.priceMonthly > 0 ? formatPrice(widgetPricing.priceMonthly) : t('packages.notSet')}
                              </span>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="w-28 text-right py-2.5 pr-3">
                            {isEditing ? (
                              <div className="flex gap-1 justify-end">
                                <button
                                  onClick={() => handleSaveModulePrice(`widget-${widget.type}`, pricingModelInput)}
                                  className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                  {t('common.save')}
                                </button>
                                <button
                                  onClick={cancelEditModulePrice}
                                  className="px-2 py-1 text-xs bg-bgLight text-textDefault rounded hover:bg-bgHover"
                                >
                                  {t('common.cancel')}
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setModulePriceInput(widgetPricing?.priceMonthly ? widgetPricing.priceMonthly.toString() : '');
                                  setPricingModelInput(widgetPricing?.pricingModel || 'monthly');
                                }}
                                className="p-1 hover:bg-bgHover rounded"
                                title={t('packages.editPrice')}
                              >
                                <PencilIcon className="w-4 h-4 text-textSecondary" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {menuSystems.length === 0 && (
            <div className="text-center py-12 bg-bgElevated rounded-xl border border-border">
              <CubeIcon className="w-16 h-16 mx-auto text-textTertiary mb-4" />
              <p className="text-textSecondary">{t('packages.noModulesFound')}</p>
            </div>
          )}
        </div>
      )}

      {/* Prispaket-flik */}
      {activeTab === 'packages' && (
      <div className="space-y-4">
        {systems.map(system => {
          const isExpanded = expandedSystems.has(system.id);
          
          return (
            <div key={system.id} className="bg-bgElevated rounded-xl border border-border overflow-hidden">
              {/* System header */}
              <div className="flex items-center justify-between p-4 border-b border-border bg-bgBase">
                <button
                  onClick={() => toggleExpand(system.id)}
                  className="flex items-center gap-3 flex-1"
                >
                  {getIcon(system.icon)}
                  <span className="font-semibold text-textDefault">{system.displayName}</span>
                  <span className="text-sm text-textSecondary">
                    ({system.packages.length} {t('packages.packagesCount')})
                  </span>
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-textTertiary ml-2" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-textTertiary ml-2" />
                  )}
                </button>
                <button
                  onClick={() => openCreateModal(system.id)}
                  className="px-3 py-1.5 bg-bgPrimary text-white rounded-lg text-sm flex items-center gap-1 hover:bg-bgPrimaryHover"
                >
                  <PlusIcon className="w-4 h-4" />
                  {t('packages.newPackage')}
                </button>
              </div>

              {/* Packages */}
              {isExpanded && (
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-3 text-textSecondary font-medium">Paket</th>
                          <th className="text-right py-2 px-3 text-textSecondary font-medium">{t('packages.pricePerMonth')}</th>
                          <th className="text-right py-2 px-3 text-textSecondary font-medium">{t('packages.pricePerYear')}</th>
                          <th className="text-left py-2 px-3 text-textSecondary font-medium">{t('packages.modules')}</th>
                          <th className="text-center py-2 px-3 text-textSecondary font-medium">Standard</th>
                          <th className="w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {system.packages.map(pkg => (
                          <tr key={pkg.id} className="border-b border-border hover:bg-bgBase">
                            <td className="py-3 px-3">
                              <div className="font-medium text-textDefault">{pkg.displayName}</div>
                              {pkg.description && (
                                <div className="text-xs text-textSecondary">{pkg.description}</div>
                              )}
                            </td>
                            <td className="py-3 px-3 text-right font-medium">{formatPrice(pkg.priceMonthly)}</td>
                            <td className="py-3 px-3 text-right text-textSecondary">{formatPrice(pkg.priceYearly)}</td>
                            <td className="py-3 px-3">
                              <div className="flex flex-wrap gap-1">
                                {pkg.modules.filter(m => m.isIncluded).slice(0, 3).map(m => (
                                  <span key={m.id} className="text-xs px-2 py-0.5 bg-bgBase text-textSecondary rounded">
                                    {m.displayName}
                                  </span>
                                ))}
                                {pkg.modules.filter(m => m.isIncluded).length > 3 && (
                                  <span className="text-xs text-textTertiary">
                                    +{pkg.modules.filter(m => m.isIncluded).length - 3}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center">
                              {pkg.isDefault && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                                  Standard
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-3">
                              <button
                                onClick={() => openEditModal(pkg)}
                                className="p-1.5 hover:bg-bgHover rounded"
                                title={t('common.edit')}
                              >
                                <PencilIcon className="w-4 h-4 text-textSecondary" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Moduler utan paket */}
        {modulesWithoutPackages.length > 0 && (
          <div className="bg-bgElevated rounded-xl border border-dashed border-border p-6">
            <h3 className="font-medium text-textDefault mb-4">
              {t('packages.modulesWithoutPackages')} ({modulesWithoutPackages.length})
            </h3>
            <div className="space-y-4">
              {Object.entries(modulesWithoutPackagesBySystem).map(([systemName, modules]) => (
                <div key={systemName}>
                  <h4 className="text-sm font-medium text-textSecondary mb-2">{systemName}</h4>
                  <div className="flex flex-wrap gap-2">
                    {modules.map(module => (
                      <span
                        key={module.id}
                        className="flex items-center gap-2 px-3 py-1.5 bg-bgBase border border-border rounded-lg text-sm text-textDefault"
                      >
                        {getIcon(module.icon || undefined, 'w-4 h-4')}
                        {module.displayName}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {systems.length === 0 && modulesWithoutPackages.length === 0 && (
          <div className="text-center py-12 bg-bgElevated rounded-xl border border-border">
            <CubeIcon className="w-16 h-16 mx-auto text-textTertiary mb-4" />
            <p className="text-textSecondary">{t('packages.noSystemsFound')}</p>
          </div>
        )}
      </div>
      )}

      {/* Modal för att skapa/redigera paket */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-bgElevated rounded-xl border border-border w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-textDefault">
                {editingPackage ? t('packages.editPackage') : t('packages.createNew')}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              {/* Namn */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.internalName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packages.codeExample')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.displayName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={e => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packages.nameExample')}
                  />
                </div>
              </div>

              {/* Beskrivning */}
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('common.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                  rows={2}
                  placeholder={t('packages.descriptionPlaceholder')}
                />
              </div>

              {/* Priser */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.priceMonthly')} (SEK) *
                  </label>
                  <input
                    type="number"
                    value={formData.priceMonthly}
                    onChange={e => setFormData(prev => ({ ...prev, priceMonthly: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder="500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.priceYearly')} (SEK)
                  </label>
                  <input
                    type="number"
                    value={formData.priceYearly}
                    onChange={e => setFormData(prev => ({ ...prev, priceYearly: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder="5000"
                  />
                  <p className="text-xs text-textTertiary mt-1">
                    {t('copy.ui.autoYearlyPriceHint')}
                  </p>
                </div>
              </div>

              {/* Begränsningar */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.maxUsers')}
                  </label>
                  <input
                    type="number"
                    value={formData.maxUsers}
                    onChange={e => setFormData(prev => ({ ...prev, maxUsers: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packages.unlimited')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textDefault mb-1">
                    {t('packages.maxVehicles')}
                  </label>
                  <input
                    type="number"
                    value={formData.maxVehicles}
                    onChange={e => setFormData(prev => ({ ...prev, maxVehicles: e.target.value }))}
                    className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                    placeholder={t('packages.unlimited')}
                  />
                </div>
              </div>

              {/* Default */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={e => setFormData(prev => ({ ...prev, isDefault: e.target.checked }))}
                  className="w-4 h-4"
                />
                <label htmlFor="isDefault" className="text-sm text-textDefault">
                  {t('packages.defaultPackage')}
                </label>
              </div>

              {/* Moduler */}
              <div>
                <label className="block text-sm font-medium text-textDefault mb-2">
                  {t('packages.includedModules')} ({t('copy.ui.modulesAvailable', { count: systemModules.length })})
                </label>
                <div className="grid grid-cols-2 gap-1 p-3 bg-bgBase border border-border rounded-lg max-h-96 overflow-y-auto">
                  {systemModules.map(module => (
                    <label
                      key={module.id}
                      className="flex items-center gap-2 p-2 hover:bg-bgHover rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.moduleIds.includes(module.id)}
                        onChange={() => toggleModule(module.id)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-textDefault">{module.displayName}</span>
                    </label>
                  ))}
                  {systemModules.length === 0 && (
                    <p className="text-sm text-textSecondary col-span-2">
                      {t('packages.noModulesFound')}
                    </p>
                  )}
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-textDefault mb-1">
                  {t('packages.extraFeatures')}
                </label>
                <textarea
                  value={formData.features}
                  onChange={e => setFormData(prev => ({ ...prev, features: e.target.value }))}
                  className="w-full px-3 py-2 bg-bgBase border border-border rounded-lg text-textDefault"
                  rows={3}
                  placeholder={t('systemPackagesManager.featuresPlaceholder')}
                />
              </div>
            </div>

            <div className="p-6 border-t border-border flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 border border-border rounded-lg text-textDefault hover:bg-bgHover"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name || !formData.displayName || formData.priceMonthly <= 0}
                className="px-4 py-2 bg-bgPrimary text-white rounded-lg hover:bg-bgPrimaryHover disabled:opacity-50"
              >
                {editingPackage ? t('common.saveChanges') : t('packages.createPackage')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
